// react
import { useEffect } from 'react';

// tanstack
import { createRootRoute, Outlet } from '@tanstack/react-router';

// material ui
import { Box, Typography, useTheme } from '@mui/material';

// shared components
import {
  initializeApplicationInsights,
  setApplicationInsightsSuperUserContext,
  setApplicationInsightsUserEmailContext,
} from '@/components/application-insights/ApplicationInsightsService';
import GlobalAlertContainer from '@/components/ui/alert/GlobalAlertContainer';
import GatewayLoadingIndicator from '@/components/ui/progress/GatewayLoadingIndicator';

// components
import AppLayout from '@/app/AppLayout';
import BreadcrumbProvider from '@/app/BreadcrumbProvider';
import ErrorBoundary from '@/routes/-ErrorBoundary.tsx';
import NotFoundComponent from '@/routes/-NotFoundComponent';

// relative
import AppBarBanner from '../components/appbar/update-banner/AppBarBanner';
import SnackbarComponent from '../components/ui/snackbar/SnackbarComponent';
import { useColorMode } from '../contexts/ColorModeContext';
import { useSessionAuth } from '../contexts/SessionAuthContext';
import { useSilentAuthGuard } from '../hooks/useSilentAuthGuard';
import AdminViewModeProvider from '../providers/AdminViewModeProvider';
import AppProvider from '../providers/AppProvider';
import AuthProvider from '../providers/AuthProvider';
import ColorModeProvider from '../providers/ColorModeProvider';
import ContainerLayoutProvider from '../providers/ContainerLayoutProvider';
import CookiePreferencesProvider from '../providers/CookiePreferencesProvider';

function RootComponent() {
  return (
    <ColorModeProvider>
      <ContainerLayoutProvider>
        <AuthProvider>
          <AdminViewModeProvider>
            <RootContent />
          </AdminViewModeProvider>
        </AuthProvider>
      </ContainerLayoutProvider>
    </ColorModeProvider>
  );
}

function RootContent() {
  useSilentAuthGuard();

  const { session, isLoading } = useSessionAuth();

  const { mode } = useColorMode();

  const theme = useTheme();

  useEffect(() => {
    initializeApplicationInsights();
  }, []);

  useEffect(() => {
    const superUserClaim = session?.claims?.['accounts.superuser'];
    const isSuperUser =
      superUserClaim === true ||
      String(superUserClaim).toLowerCase() === 'true';
    const userEmail = session?.claims?.email;

    setApplicationInsightsSuperUserContext(isSuperUser);
    setApplicationInsightsUserEmailContext(userEmail);
  }, [session]);

  if (isLoading) {
    return (
      <Box
        sx={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: theme.palette.background.default,
          color: theme.palette.text.primary,
        }}
      >
        <GatewayLoadingIndicator isLoading={isLoading} disableBackdrop={true} />
      </Box>
    );
  }

  if (!session?.authenticated)
    return (
      <Box
        sx={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: theme.palette.background.default,
          color: theme.palette.text.primary,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <Typography
            variant="h4"
            className="Re"
            sx={{
              fontFamily: 'neuropolitical',
              color: (theme) =>
                theme.palette.mode === 'light'
                  ? 'hsl(0, 0%, 20%)'
                  : 'hsl(0, 0%, 85%)',
              fontWeight: 'medium',
            }}
          >
            Re
          </Typography>
          <Typography
            variant="h4"
            className="M"
            sx={{
              fontFamily: 'neuropolitical',
              color: 'hsl(63, 100%, 40%)',
              fontWeight: 'medium',
            }}
          >
            M
          </Typography>
          <Typography
            variant="h4"
            className="oni"
            sx={{
              fontFamily: 'neuropolitical',
              color: (theme) =>
                theme.palette.mode === 'light'
                  ? 'hsl(0, 0%, 20%)'
                  : 'hsl(0, 0%, 85%)',
              fontWeight: 'medium',
            }}
          >
            oni
          </Typography>
        </Box>
        <Typography variant="body1">Redirecting to login...</Typography>
      </Box>
    );

  return (
    <AppProvider mode={mode}>
      <CookiePreferencesProvider>
        <BreadcrumbProvider>
          <AppLayout>
            <AppBarBanner />
            <GlobalAlertContainer />
            <SnackbarComponent />
            <Outlet />
          </AppLayout>
        </BreadcrumbProvider>
      </CookiePreferencesProvider>
    </AppProvider>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorBoundary,
});
